import React, { useEffect, useState } from "react";
import { Button, Input, message, Tooltip } from "antd";
import { CopyOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons";

import { buildShortform, missingShortformFields } from "./shortform";

/**
 * The at-a-glance artifact — the back of the clinic's paper form.
 *
 * Generated live from the worksheet so it can never drift from the fields, but
 * hand-editable, because the reason their sheet works is that a person can write
 * the one sentence a carrier said that no field anticipated. An edit sticks
 * until explicitly reverted, and the card says plainly which mode it is in
 * rather than silently ignoring later changes.
 *
 * Blank fields are omitted from the text, so the "still to ask" line below it is
 * what stops an unanswered question from reading as an unimportant one.
 */
export default function ShortformCard({ state, compact }) {
  const generated = buildShortform(state);
  const [override, setOverride] = useState(null); // null = tracking the fields
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(generated);

  // Keep the draft in step with the fields while no manual edit is in force.
  useEffect(
    function () {
      if (override === null) setDraft(generated);
    },
    [generated, override]
  );

  const text = override === null ? generated : override;
  const missing = missingShortformFields(state);

  function handleCopy() {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        message.success("Shortform copied");
        return;
      }
    } catch (e) { /* fall through to the notice below */ }
    message.info("Select the text to copy it");
  }

  return (
    <div className="bw-card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <p className="bw-card-title" style={{ marginBottom: 0 }}>
          Shortform
          {override !== null ? <span className="bw-count">edited by hand</span> : null}
        </p>
        <div style={{ display: "flex", gap: 6 }}>
          {override !== null ? (
            <Tooltip title="Discard the manual edit and follow the fields again">
              <Button size="small" icon={<ReloadOutlined />} onClick={function () {
                setOverride(null);
                setEditing(false);
              }}>Reset</Button>
            </Tooltip>
          ) : null}
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={function () {
              setDraft(text);
              setEditing(!editing);
            }}
          >
            {editing ? "Done" : "Edit"}
          </Button>
          <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>Copy</Button>
        </div>
      </div>

      {editing ? (
        <Input.TextArea
          autoSize={{ minRows: 5, maxRows: 14 }}
          value={draft}
          onChange={function (e) {
            setDraft(e.target.value);
            setOverride(e.target.value);
          }}
          style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 13 }}
        />
      ) : (
        <div className="bw-shortform">
          {text
            ? text.split("\n").map(function (line, i) {
                // Exception lines are starred and carried first, per their rule.
                const isException = line.trim().charAt(0) === "*";
                return (
                  <div key={i} className={isException ? "bw-exception" : ""}>
                    {line}
                  </div>
                );
              })
            : "Fill the worksheet and the shortform writes itself here."}
        </div>
      )}

      {!compact && missing.length > 0 ? (
        <p className="bw-missing">
          Still to ask ({missing.length}): {missing.join(", ")}
        </p>
      ) : null}
    </div>
  );
}
